import { useCallback, useState } from "react";
import { useCreateSpeakingClassServiceAdminAxiosInstance } from "../../Auth/useCreateSpeakingClassServiceAdminAxiosInstance.ts";
import {
  WithdrawalRequestDTO,
  WithdrawalRequestStatus,
} from "./WithdrawalRequestDTO.ts";

export function useUpsertWithdrawalRequest() {
  const { createAxiosInstance } =
    useCreateSpeakingClassServiceAdminAxiosInstance();
  const [loading, setLoading] = useState(false);

  const createRequest = useCallback(
    async ({
      userEmail,
      purchaseKey,
      requestedAtUtc,
      notes,
      notify = true,
    }: {
      userEmail: string;
      purchaseKey?: string;
      requestedAtUtc: Date;
      notes?: string;
      notify?: boolean;
    }): Promise<WithdrawalRequestDTO> => {
      setLoading(true);
      try {
        const { data } = await createAxiosInstance().post<{
          request: WithdrawalRequestDTO;
        }>("/withdrawal-requests", {
          userEmail,
          ...(purchaseKey && { purchaseKey }),
          requestedAtUtc: requestedAtUtc.toISOString(),
          ...(notes && { notes }),
          notify,
        });
        return data.request;
      } finally {
        setLoading(false);
      }
    },
    [createAxiosInstance],
  );

  const updateRequest = useCallback(
    async (
      id: string,
      changes: {
        status?: WithdrawalRequestStatus;
        requestedAtUtc?: Date;
        notes?: string;
        orderId?: string;
        dealId?: string;
      },
    ): Promise<WithdrawalRequestDTO> => {
      setLoading(true);
      try {
        const { data } = await createAxiosInstance().patch<{
          request: WithdrawalRequestDTO;
        }>(`/withdrawal-requests/${id}`, {
          ...(changes.status && { status: changes.status }),
          ...(changes.requestedAtUtc && {
            requestedAtUtc: changes.requestedAtUtc.toISOString(),
          }),
          ...(changes.notes !== undefined && { notes: changes.notes }),
          ...(changes.orderId !== undefined && { orderId: changes.orderId }),
          ...(changes.dealId !== undefined && { dealId: changes.dealId }),
        });
        return data.request;
      } finally {
        setLoading(false);
      }
    },
    [createAxiosInstance],
  );

  const resyncWithHubSpot = useCallback(
    async (id: string, notifyCloser: boolean): Promise<void> => {
      setLoading(true);
      try {
        await createAxiosInstance().post(
          `/withdrawal-requests/${id}/hubspot-sync`,
          { notifyCloser },
        );
      } finally {
        setLoading(false);
      }
    },
    [createAxiosInstance],
  );

  return { createRequest, updateRequest, resyncWithHubSpot, loading };
}
